/** Pass a task to somebody else, with an optional reason (ADR-0223, option 3B; KD decision D-29).
 *
 * An Admin's control, for `NewTask`'s reason: the list of people is Admin-only (`GET /members`), and a
 * picker nobody else can fill would be a control that predictably fails. The reason is a few words
 * the **new owner** reads on the task; the server keeps it on a history row and nowhere else - never
 * in the ledger, never in the notification that tells the new owner the task is theirs (rule 4).
 * It is optional, because a required one forces a typed sentence onto a bulk reassignment when
 * somebody leaves, which is when it is least useful.
 *
 * Q-06: the button is never greyed for a missing box; pressing it says which, beside the box.
 */

import { useState } from "react";

import { useMe } from "../api/auth";
import { useMembers } from "../api/queries";
import { useUpdateTask } from "../api/work";
import type { Task } from "../api/work";
import { FieldError } from "../components/FieldError";
import { ReadFailed, WriteFailed } from "../components/Loaded";
import { useFormChecks } from "../components/useFormChecks";
import styles from "./Screen.module.css";

/** The server's bound (`MAX_REASSIGNMENT_REASON`), said before the request. */
export const MAX_REASON = 280;

export function TaskReassign({ task, onDone }: { task: Task; onDone: () => void }) {
  const { data: me } = useMe();
  const members = useMembers(true);
  const update = useUpdateTask();
  const [owner, setOwner] = useState("");
  const [reason, setReason] = useState("");
  const [refusal, setRefusal] = useState<unknown>(null);
  const [checks, attach] = useFormChecks<HTMLDivElement>();
  const people = (members.data ?? []).filter(
    (member) => member.status === "active" && member.user_id !== task.owner_id,
  );

  async function submit() {
    setRefusal(null);
    const ok = checks.run([
      { field: "owner", ok: owner !== "", message: "Choose who the task goes to." },
      {
        field: "reason",
        ok: reason.trim().length <= MAX_REASON,
        message: `Keep the reason to ${MAX_REASON} characters. This has ${reason.trim().length}.`,
      },
    ]);
    if (!ok) return;
    try {
      await update.mutateAsync({
        taskId: task.id,
        ownerId: owner,
        ...(reason.trim() === "" ? {} : { reassignmentReason: reason.trim() }),
        expectedVersion: task.version,
      });
      onDone();
    } catch (error) {
      if (!checks.refuse(error, { owner_id: "owner", reassignment_reason: "reason" })) {
        setRefusal(error);
      }
    }
  }

  return (
    <div ref={attach} data-testid="reassign-form">
      <label className={styles.field}>
        <span>Pass it to</span>
        <select
          aria-label="Pass it to"
          {...checks.field("owner")}
          value={owner}
          onChange={(event) => {
            checks.touch("owner");
            setOwner(event.target.value);
          }}
        >
          <option value="">Choose</option>
          {people.map((member) => (
            <option key={member.user_id} value={member.user_id}>
              {member.user_id === me?.user_id ? `${member.display_name} (you)` : member.display_name}
            </option>
          ))}
        </select>
        <FieldError {...checks.say("owner")} />
      </label>
      {members.error ? (
        <ReadFailed
          of="The list of people"
          error={members.error}
          onRetry={() => void members.refetch()}
        />
      ) : null}
      <label className={styles.field}>
        <span>Why? (optional)</span>
        <textarea
          aria-label="Why is it being passed on?"
          rows={2}
          {...checks.field("reason")}
          value={reason}
          onChange={(event) => {
            checks.touch("reason");
            setReason(event.target.value);
          }}
          placeholder="The previous owner is on leave until March."
        />
        <FieldError {...checks.say("reason")} />
      </label>
      <p>
        <button
          type="button"
          className={styles.primary}
          disabled={update.isPending}
          onClick={() => void submit()}
          data-testid="save-reassign"
        >
          {update.isPending ? "Saving…" : "Pass it on"}
        </button>{" "}
        <button type="button" className={styles.fixLink} onClick={onDone}>
          Not now
        </button>
      </p>
      <WriteFailed error={refusal} of="this task" testId="reassign-refused" />
    </div>
  );
}
